import { Static, Type } from '@sinclair/typebox';

// =====================================================================
// CRM — registre de contact (refus, désabonnements) et tâches de l'équipe.
// cf. docs/to_implement/crm-suivi-apres-essai-plan.md. Enums dual-defined avec schema.prisma.
//
// Vocabulaire : « ne pas rappeler » = refus du canal appel ; « désinscrit » = refus d'un canal
// écrit ou d'une liste ; « ne plus contacter » = refus de tous les canaux. Ces états ne sont
// stockés nulle part : ils se CALCULENT sur les lignes actives du registre.
// =====================================================================

export enum CrmConsentChannel {
  CALL = 'CALL',
  SMS = 'SMS',
  EMAIL = 'EMAIL',
  WHATSAPP = 'WHATSAPP',
  ALL = 'ALL',
}

export enum CrmConsentScope {
  ALL = 'ALL',
  /** Relances Pulse, relances d'essai, appels de la file, SMS de suivi. */
  LIFECYCLE = 'LIFECYCLE',
  /** Liste de diffusion (inscrits non clients) : séquence d'emails et campagnes. */
  PROSPECTION = 'PROSPECTION',
  /** Newsletter des clients. */
  NEWSLETTER = 'NEWSLETTER',
}

export enum CrmConsentState {
  REFUSED = 'REFUSED',
  ALLOWED = 'ALLOWED',
}

export enum CrmConsentSource {
  CSM_CALL = 'CSM_CALL',
  PULSE = 'PULSE',
  SMS_STOP = 'SMS_STOP',
  EMAIL_UNSUB = 'EMAIL_UNSUB',
  ACCOUNT_DELETION_REQUEST = 'ACCOUNT_DELETION_REQUEST',
  PUBLIC_PAGE = 'PUBLIC_PAGE',
  ADMIN = 'ADMIN',
}

/** État de contact affiché sur la fiche. Exclusifs : le plus restrictif l'emporte. */
export enum CrmContactStatus {
  NORMAL = 'NORMAL',
  DO_NOT_CALL = 'DO_NOT_CALL',
  UNSUBSCRIBED = 'UNSUBSCRIBED',
  DO_NOT_CONTACT = 'DO_NOT_CONTACT',
}

/** Ce qu'on s'apprête à envoyer : la porte en déduit les couples (canal, portée) à vérifier. */
export enum CrmContactPurpose {
  /** Relance Pulse (cascade, silence) ou envoi différé de l'aperçu, hors réponse à un message. */
  PULSE_RELANCE = 'PULSE_RELANCE',
  /** La même relance Pulse, partie par email (lead sans WhatsApp). */
  PULSE_RELANCE_EMAIL = 'PULSE_RELANCE_EMAIL',
  TRIAL_RELANCE_EMAIL = 'TRIAL_RELANCE_EMAIL',
  TRIAL_RELANCE_SMS = 'TRIAL_RELANCE_SMS',
  /** Push et notification in-app de la relance d'essai. */
  TRIAL_RELANCE_APP = 'TRIAL_RELANCE_APP',
  QUEUE_CALL = 'QUEUE_CALL',
  LAST_CHANCE_SMS = 'LAST_CHANCE_SMS',
  TRAINING_SMS = 'TRAINING_SMS',
  /** Email de suite après une séance suivie (démo, formation) : suite de service, pas une liste. */
  SESSION_FOLLOWUP_EMAIL = 'SESSION_FOLLOWUP_EMAIL',
  PROSPECTION_EMAIL = 'PROSPECTION_EMAIL',
  PROSPECTION_SMS = 'PROSPECTION_SMS',
  NEWSLETTER_EMAIL = 'NEWSLETTER_EMAIL',
  NEWSLETTER_SMS = 'NEWSLETTER_SMS',
}

/** Pourquoi la porte refuse un envoi. */
export enum CrmContactBlockReason {
  CONTACT_REFUSED = 'CONTACT_REFUSED',
  /** La personne est dans la file d'appels : les relances automatiques sont suspendues. */
  IN_CALL_QUEUE = 'IN_CALL_QUEUE',
}

export enum CrmTaskType {
  MANUAL = 'MANUAL',
  ACCOUNT_DELETION = 'ACCOUNT_DELETION',
  CONSENT_REVIEW = 'CONSENT_REVIEW',
}

export enum CrmTaskStatus {
  OPEN = 'OPEN',
  DONE = 'DONE',
  CANCELLED = 'CANCELLED',
}

export enum AdminCrmTaskView {
  OPEN = 'open',
  LATE = 'late',
  CLOSED = 'closed',
}

/** Délai de traitement d'une demande de suppression de compte (K3). */
export const CRM_ACCOUNT_DELETION_TASK_DELAY_HOURS = 72;
/** Délai de vérification d'un refus détecté par Pulse (un faux positif ne doit pas traîner). */
export const CRM_CONSENT_REVIEW_TASK_DELAY_HOURS = 24;
export const CRM_CONSENT_VERBATIM_MAX_LENGTH = 2000;
export const CRM_TASK_TITLE_MAX_LENGTH = 255;

// ── Résolution du registre (fonctions pures, partagées front / back) ─────────────

/** Les champs d'une ligne du registre dont dépend la résolution. */
export interface CrmConsentLineLike {
  channel: CrmConsentChannel | `${CrmConsentChannel}`;
  scope: CrmConsentScope | `${CrmConsentScope}`;
  state: CrmConsentState | `${CrmConsentState}`;
  source: CrmConsentSource | `${CrmConsentSource}`;
  revoked_at?: Date | string | null;
}

/** Couples (canal, portée) qu'une finalité doit voir libres. Un seul refus actif suffit à bloquer. */
export const CRM_PURPOSE_CHECKS: Record<CrmContactPurpose, { channel: CrmConsentChannel; scope: CrmConsentScope }[]> = {
  [CrmContactPurpose.PULSE_RELANCE]: [{ channel: CrmConsentChannel.WHATSAPP, scope: CrmConsentScope.LIFECYCLE }],
  [CrmContactPurpose.PULSE_RELANCE_EMAIL]: [{ channel: CrmConsentChannel.EMAIL, scope: CrmConsentScope.LIFECYCLE }],
  [CrmContactPurpose.TRIAL_RELANCE_EMAIL]: [{ channel: CrmConsentChannel.EMAIL, scope: CrmConsentScope.LIFECYCLE }],
  [CrmContactPurpose.TRIAL_RELANCE_SMS]: [{ channel: CrmConsentChannel.SMS, scope: CrmConsentScope.LIFECYCLE }],
  // Aucun canal « application » dans le registre : seul un refus de TOUS les canaux le coupe
  // (« ne plus contacter », ou « plus aucune relance », portée LIFECYCLE).
  [CrmContactPurpose.TRIAL_RELANCE_APP]: [{ channel: CrmConsentChannel.ALL, scope: CrmConsentScope.LIFECYCLE }],
  [CrmContactPurpose.QUEUE_CALL]: [{ channel: CrmConsentChannel.CALL, scope: CrmConsentScope.LIFECYCLE }],
  // Le SMS de dernière chance prolonge les appels : « ne pas rappeler » le coupe aussi.
  [CrmContactPurpose.LAST_CHANCE_SMS]: [
    { channel: CrmConsentChannel.SMS, scope: CrmConsentScope.LIFECYCLE },
    { channel: CrmConsentChannel.CALL, scope: CrmConsentScope.LIFECYCLE },
  ],
  [CrmContactPurpose.TRAINING_SMS]: [{ channel: CrmConsentChannel.SMS, scope: CrmConsentScope.LIFECYCLE }],
  // Suite de service d'une séance que la personne a suivie : seul un refus de tout email (ou de
  // tout contact) la retient ; une désinscription de liste marketing ne la concerne pas.
  [CrmContactPurpose.SESSION_FOLLOWUP_EMAIL]: [{ channel: CrmConsentChannel.EMAIL, scope: CrmConsentScope.ALL }],
  [CrmContactPurpose.PROSPECTION_EMAIL]: [{ channel: CrmConsentChannel.EMAIL, scope: CrmConsentScope.PROSPECTION }],
  [CrmContactPurpose.PROSPECTION_SMS]: [{ channel: CrmConsentChannel.SMS, scope: CrmConsentScope.PROSPECTION }],
  [CrmContactPurpose.NEWSLETTER_EMAIL]: [{ channel: CrmConsentChannel.EMAIL, scope: CrmConsentScope.NEWSLETTER }],
  [CrmContactPurpose.NEWSLETTER_SMS]: [{ channel: CrmConsentChannel.SMS, scope: CrmConsentScope.NEWSLETTER }],
};

/** Finalités de relance automatique : suspendues tant que la personne est dans la file d'appels. */
export const CRM_PURPOSES_SUSPENDED_IN_QUEUE: readonly CrmContactPurpose[] = [
  CrmContactPurpose.PULSE_RELANCE,
  CrmContactPurpose.PULSE_RELANCE_EMAIL,
  CrmContactPurpose.TRIAL_RELANCE_EMAIL,
  CrmContactPurpose.TRIAL_RELANCE_SMS,
  CrmContactPurpose.TRIAL_RELANCE_APP,
];

const isActiveLine = (line: CrmConsentLineLike): boolean => line.revoked_at === null || line.revoked_at === undefined;

/** Une ligne couvre un envoi si son canal ET sa portée le couvrent (`ALL` couvre tout). */
export function crmConsentLineCovers(
  line: Pick<CrmConsentLineLike, 'channel' | 'scope'>,
  channel: CrmConsentChannel,
  scope: CrmConsentScope,
): boolean {
  const channelOk = line.channel === CrmConsentChannel.ALL || line.channel === channel;
  const scopeOk = line.scope === CrmConsentScope.ALL || line.scope === scope;
  return channelOk && scopeOk;
}

/**
 * Refus actif qui couvre l'envoi, ou `undefined`. Un refus actif l'emporte sur toute ligne
 * `ALLOWED`, quelle que soit sa date : une autorisation (forçage d'une liste) ne sert qu'à
 * départager quand aucun refus n'est actif.
 */
export function findActiveRefusal<T extends CrmConsentLineLike>(
  lines: readonly T[],
  channel: CrmConsentChannel,
  scope: CrmConsentScope,
): T | undefined {
  return lines.find(
    (line): boolean =>
      line.state === CrmConsentState.REFUSED && isActiveLine(line) && crmConsentLineCovers(line, channel, scope),
  );
}

/** Refus actif qui bloque une finalité, ou `undefined`. */
export function findRefusalForPurpose<T extends CrmConsentLineLike>(
  lines: readonly T[],
  purpose: CrmContactPurpose,
): T | undefined {
  const blanket = lines.find(
    (line): boolean =>
      line.state === CrmConsentState.REFUSED &&
      isActiveLine(line) &&
      line.channel === CrmConsentChannel.ALL &&
      line.scope === CrmConsentScope.ALL,
  );
  if (blanket) return blanket;
  for (const check of CRM_PURPOSE_CHECKS[purpose]) {
    const refusal = findActiveRefusal(lines, check.channel, check.scope);
    if (refusal) return refusal;
  }
  return undefined;
}

/** Autorisation manuelle active (ajout à une liste par l'équipe). Sans effet si un refus est actif. */
export function hasActiveAllowance(
  lines: readonly CrmConsentLineLike[],
  channel: CrmConsentChannel,
  scope: CrmConsentScope,
): boolean {
  return lines.some(
    (line): boolean =>
      line.state === CrmConsentState.ALLOWED && isActiveLine(line) && crmConsentLineCovers(line, channel, scope),
  );
}

/**
 * État de contact d'une personne, du plus restrictif au moins restrictif :
 * « ne plus contacter » (tous canaux, toutes portées) > « ne pas rappeler » (canal appel) >
 * « désinscrit » (un canal écrit ou une liste) > normal.
 */
export function computeCrmContactStatus(lines: readonly CrmConsentLineLike[]): CrmContactStatus {
  const active = lines.filter((line): boolean => line.state === CrmConsentState.REFUSED && isActiveLine(line));
  if (active.some((line): boolean => line.channel === CrmConsentChannel.ALL && line.scope === CrmConsentScope.ALL)) {
    return CrmContactStatus.DO_NOT_CONTACT;
  }
  if (active.some((line): boolean => line.channel === CrmConsentChannel.CALL)) return CrmContactStatus.DO_NOT_CALL;
  if (active.length > 0) return CrmContactStatus.UNSUBSCRIBED;
  return CrmContactStatus.NORMAL;
}

/**
 * Un désabonnement fait par la personne (lien d'un email, STOP par SMS) ne se lève pas d'office :
 * la levée n'est acceptée que si la personne a demandé elle-même la reprise (K2).
 */
export function crmRefusalNeedsPersonRequest(source: CrmConsentSource | `${CrmConsentSource}`): boolean {
  return source === CrmConsentSource.EMAIL_UNSUB || source === CrmConsentSource.SMS_STOP;
}

/** Finalités bloquées pour une personne (colonne « ce qui est coupé » du bandeau). */
export function listBlockedPurposes(lines: readonly CrmConsentLineLike[]): CrmContactPurpose[] {
  return (Object.values(CrmContactPurpose) as CrmContactPurpose[]).filter(
    (purpose): boolean => findRefusalForPurpose(lines, purpose) !== undefined,
  );
}

// ── Listes de diffusion dérivées ─────────────────────────────────────────────────

/** Cycle de vie d'une personne (une seule fiche). Calculé, jamais saisi. */
export enum CrmLifecycleStage {
  LEAD = 'LEAD',
  /** Compte créé, essai en cours. */
  TRIAL = 'TRIAL',
  /** Compte créé, essai terminé sans abonnement. */
  TRIAL_ENDED = 'TRIAL_ENDED',
  CLIENT = 'CLIENT',
  CHURNED = 'CHURNED',
}

/** Pourquoi une personne est (ou n'est pas) dans une liste de diffusion. */
export enum CrmMailingListReason {
  /** Son étape du cycle de vie l'y place (intérêt légitime B2B, K16). */
  LIFECYCLE = 'LIFECYCLE',
  /** Ajoutée à la main par l'équipe (ligne `ALLOWED`). */
  MANUAL = 'MANUAL',
  /** Un refus actif la retire (lien de désabonnement, STOP, demande à la CSM). */
  REFUSED = 'REFUSED',
  /** Son étape ne l'y place pas et personne ne l'a ajoutée. */
  NOT_ELIGIBLE = 'NOT_ELIGIBLE',
  /** Pas d'adresse email connue. */
  NO_EMAIL = 'NO_EMAIL',
}

/** Portées qui sont des listes de diffusion email. */
export const CRM_MAILING_LIST_SCOPES: readonly CrmConsentScope[] = [
  CrmConsentScope.PROSPECTION,
  CrmConsentScope.NEWSLETTER,
];

/** Étapes qui placent d'office dans chaque liste : inscrits non clients → prospection, clients → newsletter. */
export const CRM_MAILING_LIST_STAGES: Record<
  CrmConsentScope.PROSPECTION | CrmConsentScope.NEWSLETTER,
  readonly CrmLifecycleStage[]
> = {
  [CrmConsentScope.PROSPECTION]: [CrmLifecycleStage.TRIAL, CrmLifecycleStage.TRIAL_ENDED],
  [CrmConsentScope.NEWSLETTER]: [CrmLifecycleStage.CLIENT],
};

export type CrmMailingListMembership = {
  scope: CrmConsentScope;
  member: boolean;
  reason: CrmMailingListReason;
};

/**
 * Listes de diffusion email d'une personne, dérivées de son étape et de ses lignes de consentement
 * (K16-K17) : jamais stockées. Un lead jamais inscrit ou un résilié n'y entre que par ajout manuel ;
 * un refus actif retire toujours, quelle que soit l'étape.
 */
export function deriveMailingLists(
  lines: readonly CrmConsentLineLike[],
  stage: CrmLifecycleStage | `${CrmLifecycleStage}`,
  hasEmail: boolean,
): CrmMailingListMembership[] {
  return (CRM_MAILING_LIST_SCOPES as (CrmConsentScope.PROSPECTION | CrmConsentScope.NEWSLETTER)[]).map(
    (scope): CrmMailingListMembership => {
      if (findActiveRefusal(lines, CrmConsentChannel.EMAIL, scope)) {
        return { scope, member: false, reason: CrmMailingListReason.REFUSED };
      }
      if (!hasEmail) return { scope, member: false, reason: CrmMailingListReason.NO_EMAIL };
      if (hasActiveAllowance(lines, CrmConsentChannel.EMAIL, scope)) {
        return { scope, member: true, reason: CrmMailingListReason.MANUAL };
      }
      if (CRM_MAILING_LIST_STAGES[scope].includes(stage as CrmLifecycleStage)) {
        return { scope, member: true, reason: CrmMailingListReason.LIFECYCLE };
      }
      return { scope, member: false, reason: CrmMailingListReason.NOT_ELIGIBLE };
    },
  );
}

// ── Schémas d'API admin ──────────────────────────────────────────────────────────

/** Une personne du CRM : un lead, un pro, ou les deux. */
export const AdminCrmSubjectSchema = Type.Object({
  lead_id: Type.Optional(Type.Number()),
  professional_id: Type.Optional(Type.Number()),
  label: Type.String(),
});
export type AdminCrmSubject = Static<typeof AdminCrmSubjectSchema>;

export const AdminCrmConsentLineSchema = Type.Object({
  crm_consent_id: Type.Number(),
  channel: Type.Enum(CrmConsentChannel),
  scope: Type.Enum(CrmConsentScope),
  state: Type.Enum(CrmConsentState),
  source: Type.Enum(CrmConsentSource),
  verbatim: Type.Optional(Type.String()),
  set_by_name: Type.Optional(Type.String()),
  set_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'set_at', with_time: true } }),
  is_active: Type.Boolean(),
  /** La levée exige que la personne l'ait demandée (désabonnement fait par elle-même). */
  needs_person_request: Type.Boolean(),
  revoked_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'revoked_at', with_time: true } }),
  ),
  revoked_by_name: Type.Optional(Type.String()),
  revoke_reason: Type.Optional(Type.String()),
  revoked_on_person_request: Type.Boolean(),
});
export type AdminCrmConsentLine = Static<typeof AdminCrmConsentLineSchema>;

/** Opt-outs historiques portés par `account` (relance d'essai), affichés à côté du registre. */
export const AdminCrmLegacyOptOutSchema = Type.Object({
  lifecycle_emails_opt_out_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'lifecycle_emails_opt_out_at', with_time: true } }),
  ),
  lifecycle_email_bounced_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'lifecycle_email_bounced_at', with_time: true } }),
  ),
  sms_opt_out_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'sms_opt_out_at', with_time: true } }),
  ),
});

export const AdminCrmMailingListMembershipSchema = Type.Object({
  scope: Type.Enum(CrmConsentScope),
  member: Type.Boolean(),
  reason: Type.Enum(CrmMailingListReason),
});
export type AdminCrmMailingListMembership = Static<typeof AdminCrmMailingListMembershipSchema>;

export const AdminCrmContactSummarySchema = Type.Object({
  subject: AdminCrmSubjectSchema,
  status: Type.Enum(CrmContactStatus),
  /** Refus à afficher dans le bandeau (le plus restrictif), absent quand tout est normal. */
  banner: Type.Optional(AdminCrmConsentLineSchema),
  blocked_purposes: Type.Array(Type.Enum(CrmContactPurpose)),
  /** La personne est dans la file d'appels : relances automatiques suspendues. */
  in_call_queue: Type.Boolean(),
  lines: Type.Array(AdminCrmConsentLineSchema),
  legacy: AdminCrmLegacyOptOutSchema,
  /** Listes de diffusion email, dérivées (`deriveMailingLists`), jamais stockées. */
  mailing_lists: Type.Array(AdminCrmMailingListMembershipSchema),
});
export type AdminCrmContactSummary = Static<typeof AdminCrmContactSummarySchema>;

/** Pose d'un refus (ou d'une autorisation manuelle) depuis l'admin. */
export const AdminCrmConsentSetBodySchema = Type.Object({
  lead_id: Type.Optional(Type.Number()),
  professional_id: Type.Optional(Type.Number()),
  channel: Type.Enum(CrmConsentChannel),
  scope: Type.Enum(CrmConsentScope),
  state: Type.Enum(CrmConsentState),
  /** Ce que la personne a dit, ou le motif de la décision. Obligatoire : un refus sans trace ne se relit pas. */
  verbatim: Type.String({ minLength: 3, maxLength: CRM_CONSENT_VERBATIM_MAX_LENGTH }),
});
export type AdminCrmConsentSetBody = Static<typeof AdminCrmConsentSetBodySchema>;

export const AdminCrmConsentRevokeBodySchema = Type.Object({
  reason: Type.String({ minLength: 3, maxLength: CRM_CONSENT_VERBATIM_MAX_LENGTH }),
  /** À cocher quand la personne a demandé elle-même la reprise des envois. */
  person_requested: Type.Optional(Type.Boolean()),
});
export type AdminCrmConsentRevokeBody = Static<typeof AdminCrmConsentRevokeBodySchema>;

/** État de contact d'un lot de personnes (pastilles des listes). */
export const AdminCrmContactStatusBatchBodySchema = Type.Object({
  lead_ids: Type.Optional(Type.Array(Type.Number(), { maxItems: 200 })),
  professional_ids: Type.Optional(Type.Array(Type.Number(), { maxItems: 200 })),
});
export type AdminCrmContactStatusBatchBody = Static<typeof AdminCrmContactStatusBatchBodySchema>;

export const AdminCrmContactStatusBatchResponseSchema = Type.Object({
  leads: Type.Array(Type.Object({ lead_id: Type.Number(), status: Type.Enum(CrmContactStatus) })),
  professionals: Type.Array(Type.Object({ professional_id: Type.Number(), status: Type.Enum(CrmContactStatus) })),
});
export type AdminCrmContactStatusBatchResponse = Static<typeof AdminCrmContactStatusBatchResponseSchema>;

// ── Tâches ───────────────────────────────────────────────────────────────────────

export const AdminCrmTaskItemSchema = Type.Object({
  crm_task_id: Type.Number(),
  type: Type.Enum(CrmTaskType),
  title: Type.String(),
  status: Type.Enum(CrmTaskStatus),
  is_late: Type.Boolean(),
  due_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'due_at', with_time: true } }),
  subject: Type.Optional(AdminCrmSubjectSchema),
  /** Refus à vérifier (tâche de vérification) : ce que la personne a écrit. */
  crm_consent_id: Type.Optional(Type.Number()),
  consent_verbatim: Type.Optional(Type.String()),
  consent_is_active: Type.Optional(Type.Boolean()),
  created_by_name: Type.Optional(Type.String()),
  created_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
  closed_by_name: Type.Optional(Type.String()),
  closed_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'closed_at', with_time: true } }),
  ),
});
export type AdminCrmTaskItem = Static<typeof AdminCrmTaskItemSchema>;

export const AdminCrmTaskCountsSchema = Type.Object({
  open: Type.Number(),
  late: Type.Number(),
  /** Ouvertes à échéance d'ici la fin de la journée (heure de l'équipe), retard compris : le compteur du menu. */
  due: Type.Number(),
});
export type AdminCrmTaskCounts = Static<typeof AdminCrmTaskCountsSchema>;

export const AdminCrmTaskListResponseSchema = Type.Object({
  items: Type.Array(AdminCrmTaskItemSchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
  counts: AdminCrmTaskCountsSchema,
});
export type AdminCrmTaskListResponse = Static<typeof AdminCrmTaskListResponseSchema>;

export const AdminCrmTaskCreateBodySchema = Type.Object({
  title: Type.String({ minLength: 3, maxLength: CRM_TASK_TITLE_MAX_LENGTH }),
  // ISO complet (avec offset ou Z) : l'admin n'a pas de fuseau dans son JWT, le front envoie l'instant.
  due_at: Type.String(),
  lead_id: Type.Optional(Type.Number()),
  professional_id: Type.Optional(Type.Number()),
});
export type AdminCrmTaskCreateBody = Static<typeof AdminCrmTaskCreateBodySchema>;

export const AdminCrmTaskUpdateBodySchema = Type.Object({
  status: Type.Optional(Type.Enum(CrmTaskStatus)),
  title: Type.Optional(Type.String({ minLength: 3, maxLength: CRM_TASK_TITLE_MAX_LENGTH })),
  due_at: Type.Optional(Type.String()),
});
export type AdminCrmTaskUpdateBody = Static<typeof AdminCrmTaskUpdateBodySchema>;
